import Base from '../../../Core/Base.js';
import DomHelper from '../../../Core/helper/DomHelper.js';
import StringHelper from '../../../Core/helper/StringHelper.js';
import EventHelper from '../../../Core/helper/EventHelper.js';
import DomDataStore from '../../../Core/data/DomDataStore.js';

/**
 * @module Scheduler/view/mixin/TimelineDomEvents
 */

const { eventNameMap } = EventHelper;

/**
 * Mixin that handles dom events (click etc) for scheduler and rendered events.
 *
 * @mixin
 */
export default Target => class TimelineDomEvents extends (Target || Base) {
    /**
     * Fires after a click on a time axis cell
     * @event timeAxisHeaderClick
     * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
     * @param {Date} startDate The start date of the header cell
     * @param {Date} endDate The end date of the header cell
     * @param {Event} event The event object
     */

    /**
     * Fires after a double click on a time axis cell
     * @event timeAxisHeaderDblClick
     * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
     * @param {Date} startDate The start date of the header cell
     * @param {Date} endDate The end date of the header cell
     * @param {Event} event The event object
     */

    /**
     * Fires after a right click on a time axis cell
     * @event timeAxisHeaderContextMenu
     * @param {Scheduler.column.TimeAxisColumn|Scheduler.column.VerticalTimeAxisColumn} source The column object
     * @param {Date} startDate The start date of the header cell
     * @param {Date} endDate The end date of the header cell
     * @param {Event} event The event object
     */

    static get $name() {
        return 'TimelineDomEvents';
    }

    //region Default config

    static get configurable() {
        return {
            scheduledBarEvents : {
                mousedown   : 'handleScheduledBarEvent',
                mouseup     : 'handleScheduledBarEvent',
                click       : 'handleScheduledBarEvent',
                dblclick    : 'handleScheduledBarEvent',
                contextmenu : 'handleScheduledBarEvent',
                mouseover   : 'handleScheduledBarEvent',
                mouseout    : 'handleScheduledBarEvent'
            },

            schedulerEvents : {
                click       : 'handleScheduleEvent',
                dblclick    : 'handleScheduleEvent',
                contextmenu : 'handleScheduleEvent',
                mousemove   : 'handleScheduleEvent'
            }
        };
    }

    // Currently hovered events (can be parent + child)
    hoveredEvents = new Set();

    //endregion

    //region Init

    /**
     * Adds listeners for DOM events for the scheduler and its events.
     * Which events is specified in Scheduler#scheduledBarEvents and Scheduler#schedulerEvents.
     * @private
     */
    initDomEvents() {
        const me = this;

        // Set thisObj and element of the configured listener specs.
        me.scheduledBarEvents.element = me.schedulerEvents.element = me.timeAxisSubGridElement;
        me.scheduledBarEvents.thisObj = me.schedulerEvents.thisObj = me;

        // same listener used for different events
        EventHelper.on(me.scheduledBarEvents);
        EventHelper.on(me.schedulerEvents);
    }

    //endregion

    //region Event handling

    getTimeSpanMouseEventParams(eventElement, event) {
        throw new Error('Implement in subclass');
    }

    getScheduleMouseEventParams(cellData, event) {
        throw new Error('Implement in subclass');
    }

    /**
     * Wraps dom Events for rendered scheduler EventModels and fires prefixed as our events.
     * For example click -> eventclick
     * @private
     * @param event
     */
    handleScheduledBarEvent(event) {
        const
            me           = this,
            eventElement = DomHelper.up(event.target, me.eventInnerSelector);

        if (eventElement) {
            const eventName = eventNameMap[event.type] || StringHelper.capitalize(event.type);
            me.trigger(me.scheduledEventName + eventName, me.getTimeSpanMouseEventParams(eventElement, event));
        }
    }

    /**
     * Wraps dom Events for the scheduler and fires as our events.
     * For example click -> scheduleClick
     * @private
     * @param event
     */
    handleScheduleEvent(event) {
        const
            me           = this,
            eventElement = DomHelper.up(event.target, me.eventSelector),
            cellElement  = !eventElement && DomHelper.up(event.target, `.${me.timeCellCls}`),
            eventName    = eventNameMap[event.type] || StringHelper.capitalize(event.type);

        if (cellElement) {
            const clickedDate = me.getDateFromDomEvent(event, 'floor');

            if (!clickedDate) {
                return;
            }

            const
                cellData  = DomDataStore.get(cellElement),
                index     = cellData.row.dataIndex,
                tickIndex = me.timeAxis.getTickFromDate(clickedDate),
                tick      = me.timeAxis.getAt(Math.floor(tickIndex));

            if (tick) {
                me.trigger(`schedule${eventName}`, {
                    date            : clickedDate,
                    tick,
                    tickIndex,
                    tickStartDate   : tick.startDate,
                    tickEndDate     : tick.endDate,
                    tickParentIndex : tick.parentIndex,
                    row             : cellData.row,
                    index,
                    event,
                    ...me.getScheduleMouseEventParams(cellData, event)
                });
            }
        }
    }

    onElementMouseButtonEvent(event) {
        const target = DomHelper.up(event.target, '.b-sch-header-timeaxis-cell');
        if (target) {
            const
                index        = target.dataset.tickIndex,
                position     = target.parentElement.dataset.headerPosition,
                columnConfig = this.timeAxisViewModel.columnConfig[position][index],
                contextMenu  = this.features.contextMenu;

            // Skip same events with Grid context menu triggerEvent
            if (!contextMenu || event.type !== contextMenu.triggerEvent) {
                this.trigger(`timeAxisHeader${StringHelper.capitalize(event.type)}`, {
                    startDate : columnConfig.start,
                    endDate   : columnConfig.end,
                    event
                });
            }
        }
    }

    onElementClick(event) {
        this.onElementMouseButtonEvent(event);
        super.onElementClick(event);
    }

    onElementDblClick(event) {
        this.onElementMouseButtonEvent(event);
        super.onElementDblClick(event);
    }

    onElementContextMenu(event) {
        this.onElementMouseButtonEvent(event);
        super.onElementContextMenu(event);
    }

    /**
     * Relays mouseover events as eventmouseenter if over rendered event.
     * Also adds Scheduler#overScheduledEventClass to the hovered element.
     * @private
     */
    onElementMouseOver(event) {
        super.onElementMouseOver(event);

        const
            me         = this,
            { target } = event;

        // We must be over the event bar
        if (target.closest(me.eventInnerSelector) && !me.features.eventDrag?.isDragging) {
            const eventElement = target.closest(me.eventSelector);

            if (!me.hoveredEvents.has(eventElement) && !me.preventOverCls) {
                me.hoveredEvents.add(eventElement);
                eventElement.classList.add(me.overScheduledEventClass);

                const params = me.getTimeSpanMouseEventParams(eventElement, event);
                if (params) {
                    // do not fire this event if model cannot be found
                    // this can be the case for "b-sch-dragcreator-proxy" elements for example
                    me.trigger(`${me.scheduledEventName}MouseEnter`, params);
                }
            }
        }
        else if (me.hoveredEvents.size) {
            me.unhoverAll(event);
        }
    }

    /**
     * Relays mouseout events as eventmouseleave if out from rendered event.
     * Also removes Scheduler#overScheduledEventClass from the hovered element.
     * @private
     */
    onElementMouseOut(event) {
        super.onElementMouseOut(event);

        const
            me                        = this,
            { target, relatedTarget } = event,
            eventInner                = target.closest(me.eventInnerSelector),
            eventWrap                 = target.closest(me.eventSelector);

        // We must be over the event bar
        if (eventInner && me.resolveTimeSpanRecord(target) && me.hoveredEvents.has(eventWrap) && !me.features.eventDrag?.isDragging) {
            // out to child shouldn't count...
            if (relatedTarget && DomHelper.isDescendant(eventInner, relatedTarget)) {
                return;
            }

            me.unhover(eventWrap, event);
        }
    }

    unhover(element, event) {
        const me = this;

        element.classList.remove(me.overScheduledEventClass);
        me.trigger(`${me.scheduledEventName}MouseLeave`, me.getTimeSpanMouseEventParams(element, event));
        me.hoveredEvents.delete(element);
    }

    unhoverAll(event) {
        for (const element of this.hoveredEvents) {
            !element.isReleased && this.unhover(element, event);
        }
    }

    //endregion

    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement thus.
    get widgetClass() {}
};
